/* ----------------------------------------------------------------------------
 * WordPress Connector — data (fetch) layer. Thin, typed, workspace-scoped.
 *
 * Workspace scoping is SERVER-authoritative: every call rides the cookie session
 * (credentials:'include') and the API derives the workspace from it — the same
 * model as the rest of src/data/*.ts. Mutations carry the CSRF double-submit +
 * idempotency headers automatically (see lib/api.ts). The React-Query keys in
 * the panel additionally include the active workspace id so a workspace switch
 * never renders another workspace's rows (AuthContext clears the cache too, M5).
 *
 * Authorised connector endpoints called here: POST /connector/pairings,
 * GET /connector/connections, DELETE /connector/connections/{id} (soft
 * disconnect), DELETE /connector/connections/{id}?purge=1 (hard remove),
 * PUT /connector/connections/{id} (assign the site's SEO domain), and the plugin
 * download link. The domain assignment + hard-remove are the ONLY config writes;
 * publish configuration remains read-only / local (never POSTed).
 * -------------------------------------------------------------------------- */

import { api, apiRequest } from '@/lib/api';
import { CONNECTOR_ENDPOINTS, type ConnectionDto, type PairingTokenDto } from './connections-model';
import type { CategoryReadResponse } from '@/voice/actions/categorySelectModel';

/** PUT /v1/connector/connections/{id} response. */
export interface SetDomainResult {
  ok: boolean;
  id: number;
  project_id: number | null;
  /** false when the gated domain-scoping migration has not landed yet (honest no-op). */
  persisted: boolean;
}


/** Absolute URL the "Download the Connector plugin" link points at. */
export function pluginDownloadHref(): string {
  return 'https://bookedcentral.com/connectors/wordpress/download';
}

/** POST /v1/connector/pairings — mint a single-use pairing token (shown once). */
export function issuePairing(): Promise<PairingTokenDto> {
  return api.post<PairingTokenDto>(CONNECTOR_ENDPOINTS.pairings, {});
}

/** GET /v1/connector/connections — this workspace's connected sites. */
export async function listConnections(signal?: AbortSignal): Promise<ConnectionDto[]> {
  const res = await api.get<{ connections?: ConnectionDto[] } | ConnectionDto[]>(
    CONNECTOR_ENDPOINTS.connections,
    undefined,
    signal,
  );
  if (Array.isArray(res)) return res;
  return res?.connections ?? [];
}

/** DELETE /v1/connector/connections/{id} — revoke (kills secret + disables target). */
export function disconnectConnection(id: number): Promise<void> {
  return api.del<void>(CONNECTOR_ENDPOINTS.connection(id));
}

/** PUT /v1/connector/connections/{id} — assign (or clear, projectId=null) the BC
 *  SEO domain this connected site belongs to. Server re-validates that the domain
 *  belongs to the workspace; a foreign target/domain 404s. */
export function setConnectionDomain(id: number, projectId: number | null): Promise<SetDomainResult> {
  return apiRequest<SetDomainResult>(CONNECTOR_ENDPOINTS.connection(id), {
    method: 'PUT',
    body: { project_id: projectId },
  });
}

/** DELETE /v1/connector/connections/{id}?purge=1 — HARD remove: revoke the secret
 *  then physically delete the row so it no longer lingers in the list. Idempotent
 *  (an already-removed id 404s). Distinct from disconnectConnection (soft disable). */
export function removeConnection(id: number): Promise<void> {
  return apiRequest<void>(CONNECTOR_ENDPOINTS.connection(id), {
    method: 'DELETE',
    query: { purge: 1 },
  });
}


/** GET /v1/connector/connections/{id}/categories?topic=... — the connected site's
 *  EXISTING categories plus the server's best-match suggestion for the draft topic.
 *  Read-only; used to display + (when ambiguous) pick the category BEFORE a push.
 *  Never creates a category. Returns an empty list + a needs_choice suggestion when
 *  the site categories can't be read (honest degrade). */
export async function fetchConnectionCategories(
  targetId: number,
  topic: string,
  signal?: AbortSignal,
): Promise<CategoryReadResponse> {
  return api.get<CategoryReadResponse>(
    CONNECTOR_ENDPOINTS.connectionCategories(targetId),
    topic && topic.trim() !== '' ? { topic: topic.trim() } : undefined,
    signal,
  );
}
